import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deliveryRunAPI } from '../../services/api';
import './Runsheet.css';
import { sortByLoadingPosition } from '../../utils/deliveryOccupancy';

const STARTING_ADDRESS = process.env.REACT_APP_GOOGLE_MAPS_STARTING_ADDRESS || '1319 Te Aroha Gordon Road, Manawaru 3391';

// Helper to add minutes to a time string (HH:MM format) and return new time string
function addMinutesToTime(timeStr, minutesToAdd) {
  if (!timeStr) return null;
  const match = String(timeStr).match(/(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const totalMinutes = hours * 60 + minutes + minutesToAdd;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
}

// Format time with AM/PM
function formatTimeWithPeriod(timeStr) {
  if (!timeStr) return '';
  const match = String(timeStr).match(/(\d{2}):(\d{2})/);
  if (!match) return '';
  const hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${period}`;
}

// Format date for display
function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-NZ', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function RunsheetPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [etaRange, setEtaRange] = useState(null);

  // Fetch run data
  useEffect(() => {
    if (runId) {
      fetchRunData();
    }
  }, [runId]);

  const fetchRunData = async () => {
    setLoading(true);
    setError(null);
    try {
      const runData = await deliveryRunAPI.getById(runId);
      setRun(runData);

      // Calculate route info if we have deliveries
      if (runData.deliveries && runData.deliveries.length > 0) {
        calculateRouteInfo(runData);
      }
    } catch (err) {
      console.error('Error fetching delivery run:', err);
      setError('Failed to load delivery run');
    } finally {
      setLoading(false);
    }
  };

  // Calculate route duration and ETA
  const calculateRouteInfo = async (runData) => {
    if (!runData.start_time) return;

    // Group deliveries by order and sort by position (descending - highest first)
    const orderGroups = {};
    runData.deliveries.forEach((delivery) => {
      const orderId = delivery.customer_order_id;
      if (!orderGroups[orderId]) {
        orderGroups[orderId] = {
          items: [],
          maxPosition: 0
        };
      }
      if (delivery.items) {
        delivery.items.forEach((item) => {
          const position = item.loading_position || 0;
          orderGroups[orderId].items.push(item);
          if (position > orderGroups[orderId].maxPosition) {
            orderGroups[orderId].maxPosition = position;
          }
        });
      }
    });

    const stopCount = Object.keys(orderGroups).length;
    if (stopCount === 0) return;

    // Estimate ~15 minutes per stop + ~2 minutes per km for travel
    // This is a simplified estimation; for accurate ETA, Google Maps API would be needed
    const stopTimeMinutes = stopCount * 15;
    const travelTimeMinutes = 2 * 30; // Assume avg 30km total travel
    const totalDurationMinutes = stopTimeMinutes + travelTimeMinutes;

    const startTime = runData.start_time;
    const etaEarliest = addMinutesToTime(startTime, totalDurationMinutes);
    const etaLatest = addMinutesToTime(startTime, totalDurationMinutes + 60); // +1 hour buffer

    setEtaRange({
      earliest: formatTimeWithPeriod(etaEarliest),
      latest: formatTimeWithPeriod(etaLatest),
      start: formatTimeWithPeriod(startTime)
    });

    setRouteInfo({
      stopCount,
      durationMinutes: totalDurationMinutes
    });
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Go back to scheduling
  const handleBack = () => {
    navigate('/planning');
  };

  if (loading) {
    return (
      <div className="runsheet-loading">
        <div className="spinner"></div>
        <p>Loading runsheet...</p>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="runsheet-error">
        <p>{error || 'Run not found'}</p>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Planning
        </button>
      </div>
    );
  }

  // Build sorted deliveries (by position descending - highest first).
  // The shared helper matches the API's ordering, which is the order the
  // note keys (N1, N2, ...) are assigned in.
  const sortedDeliveries = sortByLoadingPosition(run.deliveries);

  // Calculate route ETA range from stored delivery ETAs if available
  const getOverallEtaRange = () => {
    if (!run.start_time) return null;
    
    // Check if we have stored ETAs on any delivery
    const deliveriesWithStoredEta = sortedDeliveries.filter(d => d.eta_start || d.eta_end);
    
    if (deliveriesWithStoredEta.length > 0) {
      // Find the earliest eta_start and latest eta_end across all deliveries
      let earliestEta = null;
      let latestEta = null;
      
      deliveriesWithStoredEta.forEach(d => {
        if (d.eta_start) {
          if (!earliestEta || d.eta_start < earliestEta) {
            earliestEta = d.eta_start;
          }
        }
        if (d.eta_end) {
          if (!latestEta || d.eta_end > latestEta) {
            latestEta = d.eta_end;
          }
        }
      });
      
      return {
        earliest: earliestEta ? formatTimeWithPeriod(earliestEta) : null,
        latest: latestEta ? formatTimeWithPeriod(latestEta) : null,
        start: formatTimeWithPeriod(run.start_time)
      };
    }
    
    // Fall back to calculated ETA
    if (etaRange) {
      return etaRange;
    }
    
    // Calculate from route info
    if (routeInfo && run.start_time) {
      const startTime = run.start_time;
      const etaEarliest = addMinutesToTime(startTime, routeInfo.durationMinutes);
      const etaLatest = addMinutesToTime(startTime, routeInfo.durationMinutes + 60);
      return {
        earliest: formatTimeWithPeriod(etaEarliest),
        latest: formatTimeWithPeriod(etaLatest),
        start: formatTimeWithPeriod(startTime)
      };
    }
    
    return null;
  };

  const overallEtaRange = getOverallEtaRange();

  // Format delivery ETA (from stored values)
  const formatDeliveryEta = (delivery) => {
    if (delivery.eta_start && delivery.eta_end) {
      return `${formatTimeWithPeriod(delivery.eta_start)} - ${formatTimeWithPeriod(delivery.eta_end)}`;
    }
    return null;
  };

  // Group items by loading position for the truck diagram
  const positionMap = {};
  sortedDeliveries.forEach((delivery) => {
    delivery.items?.forEach((item) => {
      const pos = item.loading_position;
      const lineSize = item.order_line?.size || 0;
      const lineQty = item.order_line?.qty || 0;
      positionMap[pos] = {
        productCode: item.order_line?.product?.code || '?',
        qty: lineQty,
        // The volume is size * qty. Printing qty says a 5-cube load is "1".
        cubes: lineSize * lineQty,
        customer: delivery.customer?.fullname || 'Unknown'
      };
    });
  });

  return (
    <div className="runsheet-page runsheet">
      {/* Header - visible on screen and print */}
      <div className="runsheet-header">
        <div className="runsheet-title">
          <h1>RUNSHEET</h1>
          <span className="run-code">{run.code}</span>
        </div>
        <div className="runsheet-actions screen-only">
          <button className="btn btn-secondary" onClick={handleBack}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Runsheet content */}
      <div className="runsheet-content" ref={printRef}>
        {/* Run info bar */}
        <div className="runsheet-info-bar">
          <div className="info-item">
            <span className="info-label">Date</span>
            <span className="info-value">{formatDate(run.delivery_date)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Start Time</span>
            <span className="info-value">{formatTimeWithPeriod(run.start_time)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className="info-value status-badge">{run.status_label}</span>
          </div>
          {overallEtaRange && (
            <div className="info-item">
              <span className="info-label">ETA Range</span>
              <span className="info-value eta-range">
                {overallEtaRange.earliest} — {overallEtaRange.latest}
              </span>
            </div>
          )}
        </div>

        {/* Two-column layout */}
        <div className="runsheet-columns">
          {/* Left column - Itinerary */}
          <div className="runsheet-itinerary">
            <h2>Delivery Itinerary</h2>
            <div className="itinerary-list">
              {sortedDeliveries.length === 0 ? (
                <div className="empty-itinerary">
                  <p>No deliveries scheduled</p>
                </div>
              ) : (
                sortedDeliveries.map((delivery, index) => {
                  const order = delivery.order || {};
                  const customer = delivery.customer || {};
                  const items = delivery.items || [];
                  const noteKey = order.note_key;

                  const deliveryEta = formatDeliveryEta(delivery);
                  
                  return (
                    <div key={delivery.id} className="itinerary-stop">
                      <div className="stop-header">
                        <span className="stop-number">{index + 1}</span>
                        <div className="stop-info">
                          <span className="stop-order-ref">
                            Order #{order.order_external_reference || order.id}
                          </span>
                          {noteKey && (
                            <span className="stop-note-ref">({noteKey})</span>
                          )}
                        </div>
                        <span className="stop-position">
                          Pos #{Math.max(...items.map(i => i.loading_position))}
                        </span>
                      </div>

                      <div className="stop-details">
                        {deliveryEta && (
                          <div className="detail-row eta-row">
                            <span className="detail-label">ETA:</span>
                            <span className="detail-value eta-value">{deliveryEta}</span>
                          </div>
                        )}

                        <div className="detail-row">
                          <span className="detail-label">Products:</span>
                          <div className="detail-products">
                            {items.map((item, idx) => (
                              <span key={idx} className="product-item">
                                {item.order_line?.product?.code || '?'} × {item.order_line?.cubes ?? ((item.order_line?.size || 0) * (item.order_line?.qty || 0))}
                                {item.order_line?.line_total != null && (
                                  <span className="product-price">
                                    @ ${item.order_line.unit_price} = ${item.order_line.line_total.toFixed(2)}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Address:</span>
                          <span className="detail-value">{order.delivery_address || customer.delivery_address || 'No address'}</span>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Customer:</span>
                          <span className="detail-value">{customer.fullname || 'Unknown'}</span>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Phone:</span>
                          <span className="detail-value">{customer.phone || 'No phone'}</span>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Paid:</span>
                          <span className={`detail-value paid-status ${order.payment_received ? 'yes' : 'no'}`}>
                            {order.payment_received ? 'YES' : 'NO'}
                            {!order.payment_received && order.payment_method && (
                              <span className="payment-method"> | {order.payment_method}</span>
                            )}
                          </span>
                        </div>

                        {!order.payment_received && order.order_total != null && (
                          <div className="detail-row amount-due">
                            <span className="detail-label">Amount Due:</span>
                            <span className="detail-value">${order.order_total.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Notes key section */}
            {run.notes_key && Object.keys(run.notes_key).length > 0 && (
              <div className="notes-section">
                <h3>Order Notes</h3>
                <div className="notes-list">
                  {Object.entries(run.notes_key).map(([key, text]) => (
                    <div key={key} className="note-item">
                      <span className="note-key">{key}:</span>
                      <span className="note-text">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column - Truck diagram */}
          <div className="runsheet-truck">
            <h2>Truck Loading</h2>
            <div className="truck-diagram">
              {/* Trailer section */}
              {run.number_of_positions >= 13 && (
                <div className="truck-section trailer">
                  <div className="section-label">Trailer</div>
                  <div className="positions">
                    {[13, 12, 11, 10, 9, 8].map((pos) => (
                      <div
                        key={pos}
                        className={`position ${positionMap[pos] ? 'occupied' : 'empty'}`}
                      >
                        <span className="pos-number">{pos}</span>
                        {positionMap[pos] && (
                          <div className="pos-content">
                            <span className="pos-code">{positionMap[pos].productCode}</span>
                            <span className="pos-qty">×{positionMap[pos].cubes}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Truck section */}
              <div className="truck-section truck-bed">
                <div className="section-label">Truck</div>
                <div className="positions">
                  {[7, 6, 5, 4, 3, 2, 1].map((pos) => (
                    <div
                      key={pos}
                      className={`position ${positionMap[pos] ? 'occupied' : 'empty'}`}
                    >
                      <span className="pos-number">{pos}</span>
                      {positionMap[pos] && (
                        <div className="pos-content">
                          <span className="pos-code">{positionMap[pos].productCode}</span>
                          <span className="pos-qty">×{positionMap[pos].cubes}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div className="truck-legend">
                <div className="legend-item">
                  <span className="legend-color occupied"></span>
                  <span>Loaded</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color empty"></span>
                  <span>Empty</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="runsheet-footer">
        <span>Firewood Factory Delivery Runsheet</span>
        <span>Generated: {new Date().toLocaleString('en-NZ')}</span>
      </div>
    </div>
  );
}

export default RunsheetPage;
